import { useLoaderData, useLocation, LoaderFunctionArgs } from "react-router-dom";
import { PROMO_BALANCE_REPORT } from "./fragment";
import { colDefs, formatData } from "./helpers";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { userStore } from "../../../../store/user";
import { PromoBalanceReportRow } from "./type";
import { useRef } from "react";
import { eventStore } from "../../../../store/event";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { SecondaryButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

export const promoBalanceReportLoader = async ({
  request,
}: LoaderFunctionArgs) => {
  // RP-1937: no filter in the address (e.g. a sidebar click). Don't load every sale ever;
  // ReportFilters puts its dates back in the address and this loader runs again.
  if (!new URL(request.url).searchParams.has("where")) return [];
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const { eventId } = getEventState();
  const user = getState().user;
  const permissions = getState().permissions;

  const search = new URL(request.url);
  const where = search.searchParams.get("where");

  const vendorWhere =
    permissions === "vendor"
      ? {
          _and: {
            vendor_id: {
              _in: user?.vendors,
            },
          },
        }
      : {};

  let whereVariable: any = where
    ? {
        ...JSON.parse(where),
        ...vendorWhere,
      }
    : {};

  if (organizationId && organizationId !== 0) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      organization_id: {
        _eq: organizationId,
      },
    };
  }

  if (eventId) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      event_id: {
        _in: user?.events,
      },
    };
  }

  const { data } = await client.query({
    query: PROMO_BALANCE_REPORT,
    variables: { where: whereVariable },
  });
  return data.reports_promo_balance || [];
};

export const PromoBalanceReport = () => {
  const data = useLoaderData() as PromoBalanceReportRow[];
  const rows = formatData(data);
  const tableRows = rows.filter((row) => row.username !== "Total");
  const initialUsernamesRef = useRef<{ label: string; value: string }[] | null>(null);
  if (!initialUsernamesRef.current) {
    initialUsernamesRef.current = [
      ...new Set(data.map((row) => row.username))
    ].map((username) => ({
      label: username,
      value: username
    }));
  }
  const initialLocationsRef = useRef<{ label: string; value: string }[] | null>(null);
  if (!initialLocationsRef.current) {
    const uniqueLocationIds = [...new Set(data.map((row) => row.location_id))].filter((id) => !!id);

    initialLocationsRef.current = uniqueLocationIds
        .map((id) => {
          const match = data.find((row) => row.location_id === id);
          const locationName = match?.location_name || id.toString();
          return {
            label: locationName.toString(),
            value: id.toString(),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
  }
  const totalRow = rows.filter((row) => row.username === "Total");
  const location = useLocation();
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `PromoBalanceLoadedReport_${timestamp}.csv`,
        columnSeparator: ",",
        prependContent: buildReportCsvHeader({
          reportName: "Promo Balance Loaded Report",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Promo Balance Loaded Report" />
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          includeUsernames
          usernameOptions={initialUsernamesRef.current}
          locationOptions={initialLocationsRef.current}
          includeLocations
          customQueryPaths={{
            dateFrom: "_and[0].created_at_hour",
            dateTo: "_and[1].created_at_hour",
          }}
        />
        <div className="flex">
          <div className="ml-auto">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
          </div>
        </div>
      </div>
      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            rowData={tableRows}
            columnDefs={colDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            paginationPageSize={100}
            gridOptions={gridOptions}
            pinnedBottomRowData={totalRow}
            onGridReady={(params) => (gridRef.current = params.api)} // Set GridApi reference
          />
        </div>
      </div>
    </>
  );
};
